import {
  AssetOptionsContext,
  type AssetOptionsContextProps,
} from '@contexts/asset-options'
import { ContentType } from '@entries/content-types'
import ContentfulAsset from '@entries/contentful-asset'
import { withDisplayName } from '@hocs/with-display-name'
import { Entry } from '@resolvers/entry'
import RichText from '@resolvers/rich-text'
import { defaultRichTextFieldOptionsDeprecated } from '@resolvers/rich-text/default-rich-text-field-options-deprecated'
import { contentArticleTitleOptions } from '@rich-text/contentful-content-article-rich-text'
import {
  EnhancedButton,
  EnhancedLink,
} from '@ui/enhanced-components/enhanced-components'
import {
  ENHANCED_COMPONENT_ROLE,
  ENHANCED_COMPONENT_TYPE,
} from '@ui/enhanced-components/types'
import { graphql } from 'gatsby'
import React, { Fragment } from 'react'

const assetOptions: AssetOptionsContextProps = {
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  alt: '',
}

const ContentfulContentArticleCardIconBulle: Utils.EntryFC<
  Queries.ContentfulContentArticleCardIconBulleFragment
> = ({
  content: {
    contentArticleTitle: title,
    subtitle,
    image,
    content,
    contentLinkText,
    contentLinkUrl,
  },
  fieldInspectorProps,
}) => {
  const hasFooterLink = contentLinkText && contentLinkUrl
  const isClickableCard = !contentLinkText && contentLinkUrl
  const LinkComponent = isClickableCard ? EnhancedLink : Fragment
  const clickableProps = isClickableCard
    ? {
        href: contentLinkUrl,
        className: 'flex w-full cursor-pointer hover:no-underline',
      }
    : {}

  return (
    <LinkComponent {...clickableProps}>
      <div className="relative box-border flex w-full flex-col pt-10 tablet:pt-[59px]">
        {image && (
          <div
            className="tablet:p-m absolute left-1/2 top-0 flex h-[78px] w-[78px] -translate-x-2/4 items-center justify-center rounded-[50%] bg-background-default p-2 shadow-[0_2px_5px_0_#cdcdcd] tablet:h-[118px] tablet:w-[118px]"
            {...fieldInspectorProps({
              fieldId: 'image',
            })}
          >
            <AssetOptionsContext.Provider value={assetOptions}>
              <Entry
                content={image}
                mapping={{
                  [ContentType.ContentfulAsset]: ContentfulAsset,
                }}
              />
            </AssetOptionsContext.Provider>
          </div>
        )}
        <div className="flex h-full w-full flex-col rounded-md border border-solid border-theme-default-accent2 bg-background-default text-text-default shadow-[0_5px_10px_0_rgb(35,35,35,0.06)]">
          <div className="mt-10 flex h-full flex-col justify-between p-10">
            <div>
              {title && (
                <RichText
                  {...fieldInspectorProps({
                    fieldId: 'title',
                  })}
                  content={title}
                  options={contentArticleTitleOptions}
                />
              )}
              {subtitle && (
                <div className="pt-5">
                  <RichText
                    {...fieldInspectorProps({
                      fieldId: 'subtitle',
                    })}
                    content={subtitle}
                    options={defaultRichTextFieldOptionsDeprecated}
                  />
                </div>
              )}
              {content && (
                <div className="pt-5">
                  <RichText
                    {...fieldInspectorProps({
                      fieldId: 'content',
                    })}
                    content={content}
                    options={defaultRichTextFieldOptionsDeprecated}
                  />
                </div>
              )}
            </div>
            {hasFooterLink && (
              <div className="pt-10">
                <EnhancedButton
                  {...fieldInspectorProps({
                    fieldId: 'contentLinkText',
                  })}
                  className="!m-auto !flex !min-h-[40px] !w-full !max-w-[334px] !px-8 !py-0 [&>div]:overflow-visible"
                  data-role={ENHANCED_COMPONENT_ROLE.SECONDARY}
                  data-type={ENHANCED_COMPONENT_TYPE.CTA}
                  href={contentLinkUrl}
                  variant="secondary"
                  {...fieldInspectorProps({
                    fieldId: 'contentLinkText',
                  })}
                >
                  {contentLinkText}
                </EnhancedButton>
              </div>
            )}
          </div>
        </div>
      </div>
    </LinkComponent>
  )
}

export const query = graphql`
  fragment ContentfulContentArticleCardIconBulle on Contentful4TmD9EihHakmKMkEwKaUwQ {
    ...ContentfulEntry
    contentArticleTitle: title {
      ...ContentfulContentArticleTitle
    }
    subtitle {
      ...ContentfulContentArticleSubtitle
    }
    content {
      ...ContentfulContentArticleContent
    }
    image {
      ...ContentfulDefaultAsset
    }
    contentLinkText
    contentLinkUrl
  }
`

export default withDisplayName(
  ContentfulContentArticleCardIconBulle,
  'ContentfulContentArticleCardIconBulle'
)
